import { getCookie, setCookie } from 'cookies-next';

// Cookie expiration: 1 year (in seconds)
export const COOKIE_EXP = 365 * 24 * 60 * 60;

// Domain to which the cookie will be scoped (used in production)
export const COOKIE_DOMAIN = '.schumacherhomes.com';

// Default base structure for the shared cookie
export const baseCookie = {
  StudioId: null,
  UserEmail: '',
  FirstName: '',
  LastName: '',
  AuthExpirationDateTime: null,
  SelectedLocation: null,
};

// Check if the environment is local (localhost)
export const isLocal =
  typeof window !== 'undefined' && window.location.hostname === 'localhost';

/**
 * Retrieve the studio ID from the shared cookie, if available and valid.
 */
export function getStudioIdFromSharedCookie(): number | null {
  const cookie = getCookie('MySchumacherIsShared');
  if (!cookie) return null;

  try {
    const parsed = JSON.parse(cookie as string);
    return typeof parsed.StudioId === 'number' ? parsed.StudioId : null;
  } catch (e) {
    console.error('Failed to parse MySchumacherIsShared cookie:', e);
    return null;
  }
}

/**
 * Retrieve the selected location label from the shared cookie.
 */
export function getSelectedLocationFromSharedCookie(): string | null {
  const cookie = getCookie('MySchumacherIsShared');
  if (!cookie) return null;

  try {
    const parsed = JSON.parse(cookie as string);
    return typeof parsed.SelectedLocation === 'string'
      ? parsed.SelectedLocation
      : null;
  } catch (e) {
    console.error('Failed to parse MySchumacherIsShared cookie:', e);
    return null;
  }
}

/**
 * Update only the `SelectedLocation` field in the shared cookie.
 * If the cookie doesn't exist or is corrupted, it falls back to base structure.
 */
export function setSelectedLocationInSharedCookie(
  selectedLocation: string | null,
) {
  const existing = getCookie('MySchumacherIsShared');
  let newCookie = { ...baseCookie, SelectedLocation: selectedLocation || null };

  if (existing) {
    try {
      const parsed = JSON.parse(existing as string);
      newCookie = { ...parsed, SelectedLocation: selectedLocation || null };
    } catch (e) {
      console.error('Failed to parse existing cookie. Overwriting with base.');
    }
  }

  setCookie('MySchumacherIsShared', JSON.stringify(newCookie), {
    maxAge: COOKIE_EXP,
    path: '/',
    ...(isLocal ? {} : { domain: COOKIE_DOMAIN }), // Skip domain in local dev
  });
}

/**
 * Update `StudioId` in the shared cookie.
 * Preserves existing fields unless parsing fails.
 */
export function setStudioIdInSharedCookie(studioId: number | null) {
  const existing = getCookie('MySchumacherIsShared');
  let newCookie = {
    ...baseCookie,
    StudioId: studioId,
  };

  if (existing) {
    try {
      const parsed = JSON.parse(existing as string);
      newCookie = {
        ...parsed,
        StudioId: studioId,
      };
    } catch (e) {
      console.error('Failed to parse existing cookie. Overwriting with base.');
    }
  }

  setCookie('MySchumacherIsShared', JSON.stringify(newCookie), {
    maxAge: COOKIE_EXP,
    path: '/',
    ...(isLocal ? {} : { domain: COOKIE_DOMAIN }),
  });
}

/**
 * Generic method to update one or more fields in the shared cookie.
 * Accepts a partial update and merges it with the existing cookie data.
 */
export const updateSharedCookieFields = (
  fieldsToUpdate: Record<string, any>,
) => {
  const COOKIE_NAME = 'MySchumacherIsShared';

  let current: Record<string, any> = {};

  try {
    const raw = getCookie(COOKIE_NAME);
    current = raw ? JSON.parse(raw as string) : {};
  } catch {
    current = {};
  }

  const merged = {
    ...current,
    ...fieldsToUpdate,
  };

  setCookie(COOKIE_NAME, JSON.stringify(merged), {
    maxAge: COOKIE_EXP,
    path: '/',
    ...(isLocal ? {} : { domain: COOKIE_DOMAIN }),
  });
};
